import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "./firebase";
import {
  collection,
  doc,
  onSnapshot,
  query,
  setDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { compareEventDateDesc } from "./dataAdapters";
import { normalizeEventRecord } from "./eventViewModel";
import EventList from "./components/EventList";
import EventDiscoveryFilters from "./components/EventDiscoveryFilters";
import {
  filterEventsForDiscovery,
  getAvailableEventCategories,
  getAvailableEventDateFilters,
} from "./eventDiscovery";
import {
  EVENT_FLYER_UPLOAD_ERROR_CODE,
  submitEventWithOptionalFlyer,
  validateEventFlyer,
} from "./eventMedia";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [category, setCategory] = useState("all");

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [contact, setContact] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [submissionStage, setSubmissionStage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const eventsQuery = useMemo(() => {
    return query(
      collection(db, "events"),
      where("status", "==", "approved")
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    const unsub = onSnapshot(
      eventsQuery,
      (snap) => {
        const rows = snap.docs
          .map((d) => normalizeEventRecord(d.id, d.data()))
          .sort(compareEventDateDesc);
        setEvents(rows);
        setLoadError("");
        setLoading(false);
      },
      (error) => {
        console.error("Unable to load approved events", error);
        setLoadError("Events are temporarily unavailable. Please try again shortly.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [eventsQuery, loadAttempt]);

  const dateOptions = useMemo(
    () => getAvailableEventDateFilters(events, new Date()),
    [events]
  );
  const categoryOptions = useMemo(
    () => getAvailableEventCategories(events),
    [events]
  );
  const visibleEvents = useMemo(
    () => filterEventsForDiscovery(events, {
      search,
      dateFilter,
      category,
      now: new Date(),
    }),
    [events, search, dateFilter, category]
  );

  useEffect(() => {
    if (!dateOptions.some((option) => option.value === dateFilter)) {
      setDateFilter("all");
    }
  }, [dateFilter, dateOptions]);

  useEffect(() => {
    if (category !== "all" && !categoryOptions.includes(category)) {
      setCategory("all");
    }
  }, [category, categoryOptions]);

  const clearDiscoveryFilters = () => {
    setSearch("");
    setDateFilter("all");
    setCategory("all");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert("Please add an event title.");
    if (!date) return alert("Please choose a date.");

    const flyerValidation = validateEventFlyer(file);
    if (!flyerValidation.valid) {
      setFileError(flyerValidation.error);
      return;
    }

    setSubmitting(true);
    setSubmissionError("");
    setSubmissionMessage("");

    try {
      const eventReference = doc(collection(db, "events"));
      const eventRecord = {
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        website: website.trim(),
        contact: contact.trim(),
        status: "pending",
        createdAt: serverTimestamp(),
        eventDate: new Date(`${date}T${time || "00:00"}`),
        date,
        time,
        type: "event",
      };

      await submitEventWithOptionalFlyer({
        file,
        eventId: eventReference.id,
        eventRecord,
        nonce:
          window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2),
        uploadFlyer: async (selectedFile, flyerMetadata) => {
          setSubmissionStage("Uploading flyer…");
          const storageRef = ref(storage, flyerMetadata.flyerStoragePath);
          await uploadBytes(storageRef, selectedFile, {
            contentType: flyerMetadata.flyerContentType,
          });
        },
        saveEvent: async (record) => {
          setSubmissionStage("Saving event for approval…");
          await setDoc(eventReference, record);
        },
      });

      alert("Thanks! Your event has been submitted and will appear once approved.");
      setSubmissionMessage("Your event was submitted and is awaiting approval.");
      setTitle("");
      setDate("");
      setTime("");
      setLocation("");
      setDescription("");
      setWebsite("");
      setContact("");
      setFile(null);
      setFileError("");
    } catch (err) {
      console.error(err);
      const eventWasSaved =
        err?.code === EVENT_FLYER_UPLOAD_ERROR_CODE && err?.eventSaved;
      const message = eventWasSaved
        ? "Your event details were saved for review, but the flyer could not be uploaded. Please do not submit the event again; an administrator can continue without the attachment."
        : "The event could not be submitted. Please try again.";
      setSubmissionError(message);
      alert(message);
      if (eventWasSaved) {
        setTitle("");
        setDate("");
        setTime("");
        setLocation("");
        setDescription("");
        setWebsite("");
        setContact("");
        setFile(null);
        setFileError("");
      }
    } finally {
      setSubmitting(false);
      setSubmissionStage("");
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    const validation = validateEventFlyer(selectedFile);

    if (!validation.valid) {
      setFile(null);
      setFileError(validation.error);
      event.target.value = "";
      return;
    }

    setFile(selectedFile);
    setFileError("");
    setSubmissionError("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">What&apos;s happening on the Isle of Dogs?</h1>
        <p className="text-white/70 mt-1">
          Discover local events and activities. Running something locally? Submit it below — it will appear once approved.
        </p>
      </div>

      {/* Events list */}
      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-white/70">Loading…</div>
      ) : loadError ? (
        <div role="alert" className="rounded-2xl border border-amber-200/20 bg-amber-400/10 p-4 text-amber-100">
          <p>{loadError}</p>
          <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)} className="mt-3 rounded-lg border border-amber-100/30 bg-black/20 px-3 py-2 text-sm font-semibold">
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-white/70">
          No approved events yet. Be the first to add one.
        </div>
      ) : (
        <>
          <EventDiscoveryFilters
            search={search}
            onSearchChange={setSearch}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
            dateOptions={dateOptions}
            category={category}
            onCategoryChange={setCategory}
            categoryOptions={categoryOptions}
            resultCount={visibleEvents.length}
            onClear={clearDiscoveryFilters}
          />

          <section aria-labelledby="events-results-heading" className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <h2 id="events-results-heading" className="text-lg font-semibold">Events</h2>
              <p className="text-sm text-white/55">Newest event first</p>
            </div>

            {visibleEvents.length ? (
              <EventList events={visibleEvents} className="mt-4" />
            ) : (
              <div className="mt-4 rounded-xl border border-dashed border-white/15 bg-black/20 p-5 text-center">
                <p className="font-semibold">No events match those filters.</p>
                <p className="mt-1 text-sm text-white/65">Try another date, category or search term.</p>
                <button
                  type="button"
                  onClick={clearDiscoveryFilters}
                  className="mt-4 min-h-11 rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/15"
                >
                  Show all events
                </button>
              </div>
            )}
          </section>
        </>
      )}

      {/* Submission form */}
      <div id="submit-event" className="scroll-mt-28 rounded-2xl bg-white/5 border border-white/10 p-4">
        <h2 className="font-semibold text-lg">Submit an event</h2>
        <form onSubmit={handleSubmit} className="grid gap-3 mt-3 md:grid-cols-2">
          <input
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            placeholder="Event title *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            placeholder="Location (optional)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />

          <input
            type="date"
            aria-label="Event date"
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Event time"
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />

          <input
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            placeholder="Website (optional)"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <div>
            <label htmlFor="event-contact" className="text-sm text-white/70 block mb-1">
              Contact information (optional)
            </label>
            <input
              id="event-contact"
              aria-describedby="event-contact-help"
              className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
              placeholder="Email, phone or social link"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
            <p id="event-contact-help" className="mt-1 text-xs text-white/55">
              If approved, this contact information will be published publicly with your event.
            </p>
          </div>

          <textarea
            className="md:col-span-2 bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm min-h-[110px]"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="md:col-span-2">
            <label className="text-sm text-white/70 block mb-1">
              Upload flyer / poster (PDF, image)
            </label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              aria-describedby="event-flyer-help event-flyer-error"
              className="text-sm"
            />
            <p id="event-flyer-help" className="mt-1 text-xs text-white/55">
              JPG, PNG, WEBP or PDF; smaller than 5 MB.
            </p>
            {fileError && (
              <p id="event-flyer-error" role="alert" className="mt-1 text-sm text-amber-200">
                {fileError}
              </p>
            )}
          </div>

          <div className="md:col-span-2 min-h-6" aria-live="polite">
            {submissionStage && <p className="text-sm text-white/75">{submissionStage}</p>}
            {submissionError && <p role="alert" className="text-sm text-amber-200">{submissionError}</p>}
            {submissionMessage && <p className="text-sm text-green-200">{submissionMessage}</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 px-4 py-3 rounded-xl bg-[#E63946] hover:opacity-90 disabled:opacity-60 transition font-semibold"
          >
            {submitting ? submissionStage || "Submitting…" : "Submit event for approval"}
          </button>
        </form>
      </div>
    </div>
  );
}
